import { Badge, Box, HStack, Stack, Text } from "@chakra-ui/react";
import { useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type {
  RejectionGroundDto,
  ViewDocumentAsOwnerDto,
} from "../../autogen/bff-api";
import { DownloadDocumentIconButton } from "../documents/DownloadDocumentIconButton/DownloadDocumentIconButton";
import { TipTap } from "../input/TipTap/TipTap";
import { getTiptapJsonFromMarkdown } from "../input/TipTap/markdownUtils";

interface Props {
  grounds?: RejectionGroundDto[] | null;
  reason?: string | null;
  message?: string | null;
  attachments?: ViewDocumentAsOwnerDto[] | null;
  attachmentIds?: string[] | null;
}

/** Read-only rendering of a stored rejection — shared by the tilbud and kvalifikasjon flows. */
export const RejectionDetails = ({
  grounds,
  reason,
  message,
  attachments,
  attachmentIds,
}: Props) => {
  const { t } = useTranslation();
  const messageContent = useMemo(
    () => (message ? getTiptapJsonFromMarkdown(message) : ""),
    [message],
  );

  return (
    <Stack spacing="3">
      {grounds?.length ? (
        <Stack spacing="2">
          {grounds.map((ground) => (
            <Box
              key={ground.key}
              border="1px solid"
              borderColor="red.100"
              rounded="md"
              px="3"
              py="2"
              bg="red.50"
            >
              <HStack spacing="2" align="center" flexWrap="wrap">
                <Text fontSize="sm" fontWeight="semibold" color="smText">
                  {ground.foaReference}
                </Text>
                <Badge colorScheme={ground.mandatory ? "red" : "orange"}>
                  {ground.mandatory ? t("SKAL") : t("KAN")}
                </Badge>
              </HStack>
              <Text fontSize="sm" color="smMuted">
                {ground.label}
              </Text>
            </Box>
          ))}
        </Stack>
      ) : (
        <Text fontSize="sm" color="smMuted">
          {t("Det er ikke lagret detaljer for denne avvisningen.")}
        </Text>
      )}

      {reason && (
        <StoredTextBlock title={t("Begrunnelse")}>
          <Text fontSize="sm" color="smText" whiteSpace="pre-wrap">
            {reason}
          </Text>
        </StoredTextBlock>
      )}

      {message && (
        <StoredTextBlock title={t("Melding til leverandør")}>
          <Box
            sx={{
              ".tiptap p": { marginBottom: "0.75rem" },
              ".tiptap p:last-child": { marginBottom: 0 },
              ".tiptap strong": { fontWeight: "semibold" },
            }}
          >
            <TipTap
              content={messageContent}
              editable={false}
              hasBorder={false}
              maxHeight="96"
              padding="0"
            />
          </Box>
        </StoredTextBlock>
      )}

      {attachments?.length ? (
        <Box>
          <SectionLabel>{t("Vedlegg")}</SectionLabel>
          <Stack spacing="2" mt="2">
            {attachments.map((document) => (
              <HStack
                key={document.id}
                spacing="2"
                justify="space-between"
                border="1px solid"
                borderColor="smBorder"
                rounded="md"
                px="3"
                py="2"
              >
                <Text fontSize="sm" color="smText" isTruncated>
                  {document.name}
                </Text>
                <DownloadDocumentIconButton documentId={document.id} size="sm" />
              </HStack>
            ))}
          </Stack>
        </Box>
      ) : attachmentIds?.length ? (
        <Text fontSize="sm" color="smMuted">
          {t("Vedlegg kunne ikke lastes.")}
        </Text>
      ) : null}
    </Stack>
  );
};

const SectionLabel = ({ children }: { children: ReactNode }) => (
  <Text
    fontSize="xs"
    color="smMuted"
    fontWeight="semibold"
    textTransform="uppercase"
    letterSpacing="wide"
  >
    {children}
  </Text>
);

const StoredTextBlock = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => (
  <Box>
    <Box mb="2">
      <SectionLabel>{title}</SectionLabel>
    </Box>
    <Box
      border="1px solid"
      borderColor="smBorder"
      rounded="md"
      bg="smBackground"
      px="4"
      py="3"
    >
      {children}
    </Box>
  </Box>
);
